"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-1 focus-visible:ring-ring",
  {
    variants: {
      variant: {
        default: "bg-info text-info-foreground shadow hover:bg-info/90",
        primary:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        error:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        success:
          "bg-success text-success-foreground shadow-sm hover:bg-success/90",
        warning:
          "bg-warning text-warning-foreground shadow-sm hover:bg-warning/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary-hover",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        overlay:
          "bg-foreground/80 text-background hover:bg-foreground/90 hover:text-background/90 shadow-xl",
        "image-thumbnail":
          "bg-muted ring-1 ring-border/50 hover:ring-2 hover:ring-primary shadow-sm",
        "image-variation":
          "border-2 border-transparent opacity-60 hover:opacity-100 hover:border-border data-[selected=true]:border-primary data-[selected=true]:scale-105 data-[selected=true]:opacity-100 data-[selected=true]:shadow-sm",
        "variation-dot":
          "bg-primary-foreground/50 hover:bg-primary-foreground/80 data-[selected=true]:bg-primary-foreground data-[selected=true]:scale-125 rounded-full",
        link: "text-primary underline-offset-4 hover:underline",
        gradient:
          "bg-brand-logo text-brand-logo-foreground shadow-lg shadow-brand-logo/25 transition-all hover:-translate-y-0.5 hover:bg-brand-logo-hover hover:shadow-xl hover:shadow-brand-logo/30 active:translate-y-0",
        hero: "bg-info text-info-foreground shadow-md hover:bg-info/90",
        preset:
          "border-2 border-foreground/30 bg-card text-foreground shadow-sm hover:bg-foreground/10 hover:border-foreground/50",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        md: "h-9 px-4 py-2 has-[>svg]:px-3",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        xl: "h-11 rounded-md px-8 has-[>svg]:px-6",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
        dot: "w-2.5 h-2.5 p-0 min-w-0 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const variantToSpinnerColor: Record<
  string,
  "primary" | "white" | "gray" | "info" | "warning"
> = {
  default: "white",
  primary: "white",
  destructive: "white",
  success: "white",
  error: "white",
  warning: "white",
  outline: "gray",
  secondary: "gray",
  ghost: "gray",
  overlay: "white",
  "image-thumbnail": "gray",
  "image-variation": "gray",
  "variation-dot": "white",
  link: "primary",
  gradient: "white",
  hero: "white",
  preset: "gray",
};

export interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
  loadingText?: string;
}

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  loading = false,
  loadingText,
  children,
  disabled,
  onKeyDown,
  ...props
}: ButtonProps) {
  const spinnerColor = variantToSpinnerColor[variant ?? "default"] ?? "gray";

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      (e.currentTarget as HTMLButtonElement).click();
    }
    onKeyDown?.(e);
  };

  const content = (
    <>
      {loading ? (
        <Spinner
          size={size === "xs" ? "sm" : size === "xl" ? "lg" : "sm"}
          color={spinnerColor}
          className="shrink-0"
        />
      ) : (
        leftIcon && (
          <span className="[&>svg]:size-4 [&>svg]:shrink-0 flex items-center">
            {leftIcon}
          </span>
        )
      )}
      {loading && loadingText ? loadingText : children}
      {!loading && rightIcon && (
        <span className="[&>svg]:size-4 [&>svg]:shrink-0 flex items-center">
          {rightIcon}
        </span>
      )}
    </>
  );

  if (asChild) {
    return (
      <Slot
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(
          buttonVariants({ variant, size }),
          fullWidth && "w-full",
          className
        )}
        aria-busy={loading}
        {...props}
        onKeyDown={onKeyDown}
      >
        {children}
      </Slot>
    );
  }

  return (
    <button
      type="button"
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(
        buttonVariants({ variant, size }),
        fullWidth && "w-full",
        className
      )}
      disabled={disabled || loading}
      aria-busy={loading}
      {...props}
      onKeyDown={handleKeyDown}
    >
      {content}
    </button>
  );
}

export { Button, buttonVariants };
